import { useEffect, useCallback, useRef, useState, type DragEvent } from 'react'
import { useChatStore } from '../../stores/useChatStore.js'
import { apiFetch } from '../../lib/api-client.js'
import { getSocket } from '../../lib/socket-client.js'
import { CLIENT_EVENTS } from '../../types/socket-events.js'
import { ConversationHeader } from './ConversationHeader.js'
import { MessageList } from './MessageList.js'
import { ReplyBox } from './ReplyBox.js'
import { Spinner } from '../../components/index.js'
import type { ChatMessage } from '../../types/chat.js'
import type { UserProfile } from '../../types/user.js'

/** Stable empty array — avoids new reference on every selector call */
const EMPTY_MESSAGES: ChatMessage[] = []

interface ConversationPanelProps {
  chatId: string
  users: UserProfile[]
  currentUser: UserProfile
}

export function ConversationPanel({ chatId, users, currentUser }: ConversationPanelProps) {
  const chat = useChatStore((s) => s.chats.find((c) => c.id === chatId))
  const messages = useChatStore((s) => s.messages[chatId] ?? EMPTY_MESSAGES)
  const setMessages = useChatStore((s) => s.setMessages)
  const isLoading = useChatStore((s) => s.isLoadingMessages)
  const setLoadingMessages = useChatStore((s) => s.setLoadingMessages)
  const effectRan = useRef(false)

  // Drag & drop state
  const [isDragOver, setIsDragOver] = useState(false)
  const [droppedFiles, setDroppedFiles] = useState<File[] | undefined>(undefined)
  const dragCounterRef = useRef(0)

  // Load messages and join socket room
  useEffect(() => {
    // Guard against double-fire in StrictMode
    if (effectRan.current) return
    effectRan.current = true

    setLoadingMessages(true)
    apiFetch<ChatMessage[]>(`/api/internal-chats/${chatId}/messages`)
      .then((msgs) => setMessages(chatId, msgs))
      .catch((err) => console.error('[ChatInterno] Erro ao carregar mensagens:', err))
      .finally(() => setLoadingMessages(false))

    const socket = getSocket()
    if (socket) {
      socket.emit(CLIENT_EVENTS.JOIN_CHAT, { chatId })
    }

    return () => {
      effectRan.current = false
      if (socket) {
        socket.emit(CLIENT_EVENTS.LEAVE_CHAT, { chatId })
      }
    }
  }, [chatId, setMessages, setLoadingMessages])

  // Auto mark-read when new messages arrive — find the last message from another user
  useEffect(() => {
    if (messages.length === 0) return
    // Find the last message from someone else
    let lastOtherMsg = null
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].userId !== currentUser.id) {
        lastOtherMsg = messages[i]
        break
      }
    }
    if (!lastOtherMsg) return
    const socket = getSocket()
    if (socket) {
      socket.emit(CLIENT_EVENTS.MARK_READ, { chatId, messageId: lastOtherMsg.id })
    }
  }, [messages, chatId, currentUser.id])

  const handleSend = useCallback(
    (content: string, attachments?: Array<{ url: string; type: string; name: string }>) => {
      const socket = getSocket()
      if (!socket) return
      socket.emit(CLIENT_EVENTS.SEND_MESSAGE, {
        chatId,
        content: content || undefined,
        attachments: attachments ?? undefined,
      })
    },
    [chatId],
  )

  const handleEdit = useCallback(
    (messageId: string, content: string) => {
      const socket = getSocket()
      if (!socket) return
      socket.emit(CLIENT_EVENTS.EDIT_MESSAGE, { chatId, messageId, content })
    },
    [chatId],
  )

  const handleDelete = useCallback(
    (messageId: string) => {
      const socket = getSocket()
      if (!socket) return
      socket.emit(CLIENT_EVENTS.DELETE_MESSAGE, { chatId, messageId })
    },
    [chatId],
  )

  // Drag & drop handlers — use counter to avoid flickering on child elements
  const handleDragEnter = useCallback((e: DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    dragCounterRef.current += 1
    if (dragCounterRef.current === 1) {
      setIsDragOver(true)
    }
  }, [])

  const handleDragLeave = useCallback((e: DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    dragCounterRef.current -= 1
    if (dragCounterRef.current === 0) {
      setIsDragOver(false)
    }
  }, [])

  const handleDragOver = useCallback((e: DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }, [])

  const handleDrop = useCallback((e: DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    dragCounterRef.current = 0
    setIsDragOver(false)
    const files = Array.from(e.dataTransfer.files)
    if (files.length > 0) {
      setDroppedFiles(files)
    }
  }, [])

  const handleDroppedFilesConsumed = useCallback(() => {
    setDroppedFiles(undefined)
  }, [])

  if (!chat) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner />
      </div>
    )
  }

  let displayName: string
  if (chat.type === 'group') {
    displayName = chat.name ?? 'Grupo'
  } else {
    const otherMember = chat.members?.find((m) => m.userId !== currentUser.id)
    const otherUser = otherMember ? users.find((u) => u.id === otherMember.userId) : undefined
    displayName = otherUser?.name ?? chat.name ?? 'Conversa direta'
  }

  return (
    <div
      className="relative flex h-full flex-col"
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {/* Drag overlay covering messages + reply */}
      {isDragOver && (
        <div className="absolute inset-0 z-50 flex flex-col items-center justify-center bg-(--color-primary)/10 border-2 border-dashed border-(--color-primary) rounded-lg pointer-events-none">
          <svg className="h-12 w-12 text-(--color-primary) mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 16.5V9.75m0 0l3 3m-3-3l-3 3M6.75 19.5a4.5 4.5 0 01-1.41-8.775 5.25 5.25 0 0110.233-2.33 3 3 0 013.758 3.848A3.752 3.752 0 0118 19.5H6.75z" />
          </svg>
          <span className="text-sm font-medium text-(--color-primary)">Solte os arquivos aqui</span>
        </div>
      )}

      <ConversationHeader chat={chat} displayName={displayName} />
      <MessageList
        chatId={chatId}
        messages={messages}
        currentUserId={currentUser.id}
        users={users}
        isLoading={isLoading}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />
      <ReplyBox
        onSend={handleSend}
        droppedFiles={droppedFiles}
        onDroppedFilesConsumed={handleDroppedFilesConsumed}
      />
    </div>
  )
}
